import {
  type AiNotification,
  type ScheduledActivityEntity,
  type ScheduledActivityMetadata,
} from 'clients/CallhomeApiTypes';
import { getRepeatOptionValue } from 'utils/RecurrenceUtil';
import { type CamerasDictionaryCamera } from 'utils/types';
import {
  REPEAT_EVERY_15MIN,
  REPEAT_EVERY_30MIN,
  REPEAT_EVERY_4H,
  REPEAT_EVERY_6H,
  REPEAT_HOURLY,
} from 'vd-constants';
import type {
  AgentDataSource,
  AgentSchedule,
  DBWeeklySchedule,
  EventSnapshotAnchor,
  OutputSchemaField,
} from 'vd-server/db/schema';
import { type AgentData } from '../AgentsPage/hooks/useAgent';

/**
 * Converts an existing spot check into a pre-filled agent draft for the
 * `custom-image-search` template. The result is merged over the template
 * defaults in `AgentCreation`, so we only set the fields a spot check can
 * meaningfully provide (name, prompt, structured output, cameras, schedule).
 *
 * The agent model is richer than a spot check, so several mappings are
 * best-effort. See `SPOTCHECK_TO_AGENT_GAPS.md` for the known lossy edges.
 */
export type AgentSeed = Partial<
  Pick<
    AgentData,
    'name' | 'condition' | 'outputSchema' | 'dataSources' | 'schedule' | 'analysisMode'
  >
>;

/** Max magnitude for image-mode `snapshotOffsetSeconds` (vd-server/db/schema). */
const MAX_EVENT_OFFSET_SECONDS = 300;

type AnswerState = NonNullable<
  NonNullable<ScheduledActivityMetadata['aiChatAnswerStates']>[number]
>;

/** A spot check's answer states are mutually-exclusive outcomes, so they map
 *  to a single `enum` output field whose values are the states. */
function answerStatesToOutputSchema(states: AnswerState[] | undefined): OutputSchemaField[] {
  if (!states?.length) return [];
  return [
    {
      name: 'result',
      type: 'enum',
      description: 'The result of the check.',
      enumValues: states.map((state) => ({
        value: state.name,
        description: state.description,
      })),
    },
  ];
}

function cameraIdsToDataSources(
  cameraIds: readonly string[] = [],
  camerasById: Record<string, CamerasDictionaryCamera>,
): AgentDataSource[] {
  return cameraIds.map((cameraId) => ({
    type: 'camera',
    cameraId,
    siteId: camerasById[cameraId]?.siteId,
    lowLatency: false,
  }));
}

/** Operating window the `custom-image-search` template seeds by default:
 *  all seven days, 09:00–17:00. */
function defaultOperatingDays(): DBWeeklySchedule['days'] {
  return Array.from({ length: 7 }, (_, i) => ({
    id: i as never,
    enabled: true,
    start: '09:00',
    end: '17:00',
  }));
}

/**
 * Maps a spot check cron rule to the agent weekly schedule's repeat cadence.
 * The agent only supports `once`/`minutes`/`hours` within a daily window, so
 * sub-hourly and hourly spot checks map cleanly while daily/weekly/monthly/
 * annual cadences collapse to a single run per window (`repeat: 'once'`).
 */
function cronToWeeklySchedule(
  rule: string,
  reoccurringCount: number,
  weeklyScheduleId: 'aiInferredOffHours' | null | undefined,
  inSchedulePeriods: boolean | undefined,
): DBWeeklySchedule {
  let repeat: DBWeeklySchedule['repeat'];
  let repeatFrequency: number | undefined;

  switch (getRepeatOptionValue(rule, reoccurringCount)) {
    case REPEAT_EVERY_15MIN:
      repeat = 'minutes';
      repeatFrequency = 15;
      break;
    case REPEAT_EVERY_30MIN:
      repeat = 'minutes';
      repeatFrequency = 30;
      break;
    case REPEAT_HOURLY:
      repeat = 'hours';
      repeatFrequency = 1;
      break;
    case REPEAT_EVERY_4H:
      repeat = 'hours';
      repeatFrequency = 4;
      break;
    case REPEAT_EVERY_6H:
      repeat = 'hours';
      repeatFrequency = 6;
      break;
    default:
      // daily / weekly / monthly / annually have no native agent cadence —
      // run once per operating window.
      repeat = 'once';
      repeatFrequency = undefined;
  }

  // Spot checks encode location-hours timing as `aiInferredOffHours` plus
  // `inSchedulePeriods`: false = during open hours, true = during closed hours.
  const scheduleConstraint =
    weeklyScheduleId === 'aiInferredOffHours'
      ? inSchedulePeriods
        ? ('outside_operating_hours' as const)
        : ('within_operating_hours' as const)
      : undefined;

  return {
    type: 'weekly',
    days: defaultOperatingDays(),
    repeat,
    repeatFrequency,
    scheduleConstraint,
  };
}

function spotCheckTimeTargetToSnapshotAnchor(timeTarget: string | undefined): EventSnapshotAnchor {
  return timeTarget === 'END' ? 'event_end' : 'event_start';
}

/**
 * Maps an event spot check's time target and timestamp offset onto the
 * image-mode event schedule fields the agent UI edits (`snapshotAnchor`,
 * `snapshotOffsetSeconds`). Spot checks add offset ms to START or END; the
 * agent stores the same anchor plus signed seconds (−300…300).
 */
function offsetToEventSchedule(
  timeTarget: string | undefined,
  offsetMs = 0,
  reportTemplateId = '',
): AgentSchedule {
  const signedSeconds = Math.round(offsetMs / 1000);
  const snapshotOffsetSeconds = Math.min(
    MAX_EVENT_OFFSET_SECONDS,
    Math.max(-MAX_EVENT_OFFSET_SECONDS, signedSeconds),
  );

  const schedule: Extract<AgentSchedule, { type: 'event' }> = {
    type: 'event',
    snapshotAnchor: spotCheckTimeTargetToSnapshotAnchor(timeTarget),
    snapshotOffsetSeconds,
  };

  if (reportTemplateId) {
    schedule.templateIds = [reportTemplateId];
  }

  return schedule;
}

/** Fields a scheduled spot check (or the form's in-flight `newActivity`) must
 *  expose to be converted. */
export type ScheduledSpotCheckInput = Pick<
  ScheduledActivityEntity,
  'title' | 'cameraIds' | 'rule' | 'reoccurringCount' | 'metadata'
>;

export function scheduledSpotCheckToAgentSeed(
  spotCheck: ScheduledSpotCheckInput,
  camerasById: Record<string, CamerasDictionaryCamera>,
): AgentSeed {
  return {
    name: spotCheck.title,
    condition: { description: spotCheck.metadata?.aiChatPrompt },
    outputSchema: answerStatesToOutputSchema(spotCheck.metadata?.aiChatAnswerStates),
    dataSources: cameraIdsToDataSources(spotCheck.cameraIds, camerasById),
    schedule: cronToWeeklySchedule(
      spotCheck.rule,
      spotCheck.reoccurringCount,
      spotCheck.metadata?.weeklyScheduleId,
      spotCheck.metadata?.inSchedulePeriods,
    ),
  };
}

export function eventSpotCheckToAgentSeed(eventSpotCheck: AiNotification): AgentSeed {
  return {
    name: eventSpotCheck.metadata?.title,
    condition: { description: eventSpotCheck.aiChatPrompt },
    outputSchema: answerStatesToOutputSchema(eventSpotCheck.metadata?.aiChatAnswerStates),
    // Event spot checks carry no camera list; the user picks monitored areas.
    dataSources: [],
    schedule: offsetToEventSchedule(
      eventSpotCheck.aiChatTimeTarget,
      eventSpotCheck.aiChatTimestampOffset,
      eventSpotCheck.reportTemplateId,
    ),
  };
}
